import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import { Calendar, Clock, User, LogIn, LogOut, Plus, Check, X, Users, CalendarDays, BarChart3, ChevronLeft, ChevronRight, Edit, FileText, Download, Trash2, Pencil, Settings, TrendingUp, Bell, BellOff } from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Textarea } from "./components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { toast } from "sonner";
import { Toaster } from "./components/ui/toaster";

// Import refactored components
import TimeTrackingDashboard from "./components/dashboard/TimeTrackingDashboard";
import StatisticsPanel from "./components/dashboard/StatisticsPanel";
import TeamAbsencesPanel from "./components/dashboard/TeamAbsencesPanel";
import VacationsPanel from "./components/vacations/VacationsPanel";
import CalendarView from "./components/calendar/CalendarView";
import HolidayManagement from "./components/admin/HolidayManagement";
import VacationReportsAdmin from "./components/reports/VacationReportsAdmin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  // Auth state
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ 
    email: '', password: '', name: '', role: 'employee', department: 'general', manager_id: ''
  });
  const [availableManagers, setAvailableManagers] = useState([]);

  // Time tracking state
  const [timeStatus, setTimeStatus] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [timeReports, setTimeReports] = useState(null);
  const [allEmployeesReport, setAllEmployeesReport] = useState(null);
  const [personalReport, setPersonalReport] = useState(null);

  // Vacation state
  const [vacationRequests, setVacationRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedTeamRequests, setApprovedTeamRequests] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [monthlyCalendar, setMonthlyCalendar] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // Admin state
  const [allEmployees, setAllEmployees] = useState([]);
  const [vacationsSummary, setVacationsSummary] = useState(null);
  const [employeesForReports, setEmployeesForReports] = useState([]);
  const [reportForm, setReportForm] = useState({ employee_id: '', start_date: '', end_date: '', format: 'pdf' });
  const [reportPreview, setReportPreview] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeEditForm, setEmployeeEditForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // API client
  const apiClient = axios.create({
    baseURL: API,
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  });

  // Auth effects
  useEffect(() => {
    if (token) {
      fetchUserData();
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      fetchTimeStatus();
      fetchVacationRequests();
      fetchTimeReports();
      fetchTimeEntries();
      fetchPersonalReport();
      
      if (user.role === 'manager' || user.role === 'admin') {
        fetchPendingRequests();
        fetchApprovedTeamRequests();
        fetchAllEmployeesReport();
        fetchEmployeesForReports();
        fetchAllEmployees();
        fetchVacationsSummary();
      }
      fetchCalendarData();
      fetchMonthlyCalendar();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchMonthlyCalendar();
    }
  }, [currentMonth, currentYear, user]);

  // Auth functions
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      apiClient.defaults.headers['Authorization'] = `Bearer ${access_token}`;
      toast.success('¡Bienvenido!');
    } catch (error) {
      toast.error('Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const registrationData = { ...registerForm };
      if (registrationData.role !== 'employee' || !registrationData.manager_id) {
        delete registrationData.manager_id;
      }
      
      const response = await axios.post(`${API}/auth/register`, registrationData);
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      apiClient.defaults.headers['Authorization'] = `Bearer ${access_token}`;
      toast.success('¡Registro exitoso!');
    } catch (error) {
      toast.error('Error en el registro');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setTimeStatus(null);
    setVacationRequests([]);
    setPendingRequests([]);
    setCalendarData([]);
  };

  const fetchAvailableManagers = async () => {
    try {
      if (availableManagers.length === 0) {
        const response = await axios.get(`${API}/public/managers`);
        setAvailableManagers(response.data);
      }
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  // Data fetching functions
  const fetchUserData = async () => {
    try {
      const response = await apiClient.get('/users/me');
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      handleLogout();
    }
  };

  const fetchTimeStatus = async () => {
    try {
      const response = await apiClient.get('/time/status');
      setTimeStatus(response.data);
    } catch (error) {
      console.error('Error fetching time status:', error);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      const response = await apiClient.get('/time/entries');
      setTimeEntries(response.data);
    } catch (error) {
      console.error('Error fetching time entries:', error);
    }
  };

  const fetchTimeReports = async () => {
    try {
      const response = await apiClient.get('/time/summary');
      setTimeReports(response.data);
    } catch (error) {
      console.error('Error fetching time reports:', error);
    }
  };

  const fetchAllEmployeesReport = async () => {
    try {
      const response = await apiClient.get('/time/all-employees');
      setAllEmployeesReport(response.data);
    } catch (error) {
      console.error('Error fetching all employees report:', error);
    }
  };

  const fetchPersonalReport = async () => {
    try {
      const response = await apiClient.get('/reports/personal');
      setPersonalReport(response.data);
    } catch (error) {
      console.error('Error fetching personal report:', error);
    }
  };

  const fetchVacationRequests = async () => {
    try {
      const response = await apiClient.get('/vacations/my-requests');
      setVacationRequests(response.data);
    } catch (error) {
      console.error('Error fetching vacation requests:', error);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const response = await apiClient.get('/vacations/pending');
      setPendingRequests(response.data);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    }
  };

  const fetchApprovedTeamRequests = async () => {
    try {
      const response = await apiClient.get('/vacations/approved-team');
      setApprovedTeamRequests(response.data);
    } catch (error) {
      console.error('Error fetching approved team requests:', error);
    }
  };

  const fetchCalendarData = async () => {
    try {
      const response = await apiClient.get('/vacations/calendar');
      setCalendarData(response.data);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  };

  const fetchMonthlyCalendar = async () => {
    try {
      const response = await apiClient.get(`/vacations/calendar-monthly?year=${currentYear}&month=${currentMonth}`);
      setMonthlyCalendar(response.data);
    } catch (error) {
      console.error('Error fetching monthly calendar:', error);
    }
  };

  const fetchAllEmployees = async () => {
    try {
      const response = await apiClient.get('/admin/employees');
      setAllEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchVacationsSummary = async () => {
    try {
      const response = await apiClient.get('/reports/vacations-summary');
      setVacationsSummary(response.data);
    } catch (error) {
      console.error('Error fetching vacations summary:', error);
    }
  };

  const fetchEmployeesForReports = async () => {
    try {
      const response = await apiClient.get('/reports/employees');
      setEmployeesForReports(response.data);
    } catch (error) {
      console.error('Error fetching employees for reports:', error);
    }
  };

  // Action handlers
  const handleClockIn = async () => {
    setLoading(true);
    try {
      await apiClient.post('/time/clock', { action: 'clock_in' });
      await fetchTimeStatus();
      await fetchTimeEntries();
      toast.success('¡Entrada registrada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar entrada');
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    setLoading(true);
    try {
      await apiClient.post('/time/clock', { action: 'clock_out' });
      await fetchTimeStatus();
      await fetchTimeEntries();
      toast.success('¡Salida registrada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar salida');
    } finally {
      setLoading(false);
    }
  };

  const handleManualEntry = async (entryData) => {
    setLoading(true);
    try {
      await apiClient.post('/time/manual-entry', entryData);
      await fetchTimeReports();
      await fetchTimeEntries();
      await fetchTimeStatus();
      if (user.role === 'manager' || user.role === 'admin') {
        await fetchAllEmployeesReport();
      }
      toast.success('¡Jornada registrada exitosamente!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar jornada');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEntry = async (entryId, entryData) => {
    setLoading(true);
    try {
      await apiClient.put(`/time/entries/${entryId}`, entryData);
      await fetchTimeEntries();
      await fetchTimeReports();
      toast.success('Registro actualizado correctamente');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar registro');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    setLoading(true);
    try {
      await apiClient.delete(`/time/entries/${entryId}`);
      await fetchTimeEntries();
      await fetchTimeReports();
      toast.success('Registro eliminado correctamente');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar registro');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleVacationRequest = async (vacationForm) => {
    setLoading(true);
    try {
      await apiClient.post('/vacations/request', vacationForm);
      await fetchVacationRequests();
      await fetchUserData();
      toast.success('¡Solicitud de vacaciones enviada!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al solicitar vacaciones');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalAction = async (requestId, status, comments = '') => {
    setLoading(true);
    try {
      await apiClient.put(`/vacations/${requestId}/approve`, { status, manager_comments: comments });
      await fetchPendingRequests();
      await fetchCalendarData();
      await fetchMonthlyCalendar();
      toast.success(`Solicitud ${status === 'approved' ? 'aprobada' : 'rechazada'}`);
      return true;
    } catch (error) {
      toast.error('Error al procesar solicitud');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleCancelVacation = async (requestId) => {
    setLoading(true);
    try {
      const response = await apiClient.put(`/vacations/${requestId}/cancel`);
      await fetchVacationRequests();
      // Only fetch team requests if user is manager/admin
      if (user?.role === 'manager' || user?.role === 'admin') {
        await fetchApprovedTeamRequests();
      }
      await fetchCalendarData();
      await fetchMonthlyCalendar();
      await fetchUserData();
      toast.success(response.data.message || 'Vacaciones anuladas correctamente');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al anular vacaciones');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (increment) => {
    let newMonth = currentMonth + increment;
    let newYear = currentYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    }
    
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };

  const handleEditEmployee = async (employee) => {
    setEditingEmployee(employee);
    setEmployeeEditForm({
      name: employee.name,
      email: employee.email,
      department: employee.department,
      role: employee.role,
      vacation_days: employee.vacation_days,
      manager_id: employee.manager_id || '',
      municipality: employee.municipality || '',
      new_password: ''
    });
    // Load managers if not already loaded
    if (availableManagers.length === 0) {
      await fetchAvailableManagers();
    }
  };

  const handleUpdateEmployee = async () => {
    setLoading(true);
    try {
      const updateData = { ...employeeEditForm };
      Object.keys(updateData).forEach(key => {
        if (!updateData[key] && updateData[key] !== 0) delete updateData[key];
      });
      await apiClient.put(`/admin/employees/${editingEmployee.id}`, updateData);
      toast.success('Empleado actualizado correctamente');
      setEditingEmployee(null);
      setEmployeeEditForm({});
      await fetchAllEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar empleado');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmployee = async (employee) => {
    setLoading(true);
    try {
      await apiClient.delete(`/admin/employees/${employee.id}`);
      toast.success(`Empleado ${employee.name} eliminado correctamente`);
      setDeleteConfirm(null);
      await fetchAllEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar empleado');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleReminder = async (userId, currentState) => {
    try {
      const response = await apiClient.put(`/alerts/toggle-hours-reminder/${userId}`);
      toast.success(response.data.message);
      await fetchAllEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar recordatorios');
    }
  };

  const handleReportDownload = async (format) => {
    if (!reportForm.employee_id || !reportForm.start_date || !reportForm.end_date) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoadingReport(true);
    try {
      const response = await apiClient.post(`/reports/generate/${format}`, {
        employee_id: reportForm.employee_id,
        start_date: reportForm.start_date,
        end_date: reportForm.end_date
      }, { responseType: 'blob' });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const employee = employeesForReports.find(emp => emp.id === reportForm.employee_id);
      const fileName = `informe_${employee?.name?.replace(' ', '_')}_${reportForm.start_date}_${reportForm.end_date}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Informe ${format.toUpperCase()} descargado exitosamente`);
    } catch (error) {
      toast.error('Error al descargar el informe');
    } finally {
      setLoadingReport(false);
    }
  };

  const isManager = user?.role === 'manager' || user?.role === 'admin';

  // Login/Register UI
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Toaster />
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-slate-800 flex items-center justify-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_worktime-tracker-29/artifacts/3lbzkds9_MARCA_enermat_mini_2025.png" 
                alt="ENERMAT" 
                className="h-10 w-auto"
              />
              WorkTime Pro by ENERMAT
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!showRegister ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Contraseña</Label>
                  <Input
                    id="password"
                    type="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                </Button>
                <Button type="button" variant="outline" className="w-full" onClick={() => setShowRegister(true)}>
                  Crear cuenta nueva
                </Button>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nombre completo</Label>
                  <Input
                    id="name"
                    value={registerForm.name}
                    onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="reg-email">Email</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="reg-password">Contraseña</Label>
                  <Input
                    id="reg-password"
                    type="password"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="role">Rol</Label>
                  <select 
                    id="role"
                    className="w-full p-2 border rounded-md"
                    value={registerForm.role}
                    onChange={(e) => {
                      setRegisterForm({...registerForm, role: e.target.value, manager_id: ''});
                      if (e.target.value === 'employee') fetchAvailableManagers();
                    }}
                  >
                    <option value="employee">Empleado</option>
                    <option value="manager">Gerente</option>
                  </select>
                </div>
                {registerForm.role === 'employee' && (
                  <div>
                    <Label htmlFor="manager">Gerente Asignado</Label>
                    <select 
                      id="manager"
                      className="w-full p-2 border rounded-md"
                      value={registerForm.manager_id}
                      onChange={(e) => setRegisterForm({...registerForm, manager_id: e.target.value})}
                    >
                      <option value="">Seleccionar gerente...</option>
                      {availableManagers.map((manager) => (
                        <option key={manager.id} value={manager.id}>
                          {manager.name} - {manager.department}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <Label htmlFor="department">Departamento</Label>
                  <Input
                    id="department"
                    value={registerForm.department}
                    onChange={(e) => setRegisterForm({...registerForm, department: e.target.value})}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Registrando...' : 'Registrarse'}
                </Button>
                <Button type="button" variant="outline" className="w-full" onClick={() => setShowRegister(false)}>
                  Volver al login
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Toaster />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_worktime-tracker-29/artifacts/3lbzkds9_MARCA_enermat_mini_2025.png" 
                alt="ENERMAT" 
                className="h-8 w-auto"
              />
              <h1 className="text-2xl font-bold text-slate-800">WorkTime Pro by ENERMAT</h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="font-medium text-slate-800">{user.name}</p>
                <p className="text-sm text-slate-500">{user.role} • {user.department}</p>
              </div>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className={`grid w-full ${isManager ? 'grid-cols-8' : 'grid-cols-4'}`}>
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="vacations" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span className="hidden sm:inline">Vacaciones</span>
            </TabsTrigger>
            <TabsTrigger value="statistics" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              <span className="hidden sm:inline">Estadísticas</span>
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              <span className="hidden sm:inline">Calendario</span>
            </TabsTrigger>
            {isManager && (
              <>
                <TabsTrigger value="reports" className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  <span className="hidden sm:inline">Informes</span>
                </TabsTrigger>
                <TabsTrigger value="vacation-reports" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">Inf. Vac.</span>
                </TabsTrigger>
                <TabsTrigger value="holidays" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  <span className="hidden sm:inline">Festivos</span>
                </TabsTrigger>
                <TabsTrigger value="employees" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">Gestión</span>
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {isManager ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <TimeTrackingDashboard
                    user={user}
                    timeStatus={timeStatus}
                    timeEntries={timeEntries}
                    timeReports={timeReports}
                    allEmployees={allEmployees}
                    loading={loading}
                    onClockIn={handleClockIn}
                    onClockOut={handleClockOut}
                    onManualEntry={handleManualEntry}
                    onUpdateEntry={handleUpdateEntry}
                    onDeleteEntry={handleDeleteEntry}
                    onRefresh={() => { fetchTimeEntries(); fetchTimeReports(); }}
                  />
                </div>
                <div className="lg:col-span-1">
                  <TeamAbsencesPanel 
                    calendarData={calendarData}
                    user={user}
                  />
                </div>
              </div>
            ) : (
              <TimeTrackingDashboard
                user={user}
                timeStatus={timeStatus}
                timeEntries={timeEntries}
                timeReports={timeReports}
                allEmployees={allEmployees}
                loading={loading}
                onClockIn={handleClockIn}
                onClockOut={handleClockOut}
                onManualEntry={handleManualEntry}
                onUpdateEntry={handleUpdateEntry}
                onDeleteEntry={handleDeleteEntry}
                onRefresh={() => { fetchTimeEntries(); fetchTimeReports(); }}
              />
            )}
          </TabsContent>

          {/* Vacations Tab */}
          <TabsContent value="vacations">
            <VacationsPanel
              user={user}
              vacationRequests={vacationRequests}
              pendingRequests={pendingRequests}
              approvedTeamRequests={approvedTeamRequests}
              loading={loading}
              onRequestVacation={handleVacationRequest}
              onApproveRequest={handleApprovalAction}
              onRejectRequest={handleApprovalAction}
              onCancelRequest={handleCancelVacation}
            />
          </TabsContent>

          {/* Statistics Tab */}
          <TabsContent value="statistics">
            <StatisticsPanel
              timeReports={timeReports}
              personalReport={personalReport}
              allEmployeesReport={allEmployeesReport}
              user={user}
            />
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            <CalendarView
              monthlyCalendar={monthlyCalendar}
              currentMonth={currentMonth}
              currentYear={currentYear}
              onChangeMonth={changeMonth}
            />
          </TabsContent>

          {/* Manager/Admin only tabs */}
          {isManager && (
            <>
              {/* Reports Tab */}
              <TabsContent value="reports">
                <Card>
                  <CardHeader>
                    <CardTitle>Generar Informes de Empleados</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label>Empleado</Label>
                        <Select value={reportForm.employee_id} onValueChange={(v) => setReportForm({...reportForm, employee_id: v})}>
                          <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                          <SelectContent>
                            {employeesForReports.map(emp => (
                              <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Fecha Inicio</Label>
                        <Input type="date" value={reportForm.start_date} onChange={(e) => setReportForm({...reportForm, start_date: e.target.value})} />
                      </div>
                      <div>
                        <Label>Fecha Fin</Label>
                        <Input type="date" value={reportForm.end_date} onChange={(e) => setReportForm({...reportForm, end_date: e.target.value})} />
                      </div>
                      <div className="flex items-end gap-2">
                        <Button onClick={() => handleReportDownload('pdf')} disabled={loadingReport}>
                          <Download className="h-4 w-4 mr-2" />PDF
                        </Button>
                        <Button variant="outline" onClick={() => handleReportDownload('excel')} disabled={loadingReport}>
                          <Download className="h-4 w-4 mr-2" />Excel
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Vacation Reports Tab */}
              <TabsContent value="vacation-reports">
                <VacationReportsAdmin token={token} user={user} />
              </TabsContent>

              {/* Holidays Management Tab */}
              <TabsContent value="holidays">
                <HolidayManagement token={token} user={user} />
              </TabsContent>

              {/* Employee Management Tab */}
              <TabsContent value="employees">
                <Card>
                  <CardHeader>
                    <CardTitle>Gestión de Empleados</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nombre</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Departamento</TableHead>
                          <TableHead>Población</TableHead>
                          <TableHead>Rol</TableHead>
                          <TableHead>Manager</TableHead>
                          <TableHead>Vacaciones</TableHead>
                          <TableHead>Recordatorios</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allEmployees.map(emp => (
                          <TableRow key={emp.id}>
                            <TableCell className="font-medium">{emp.name}</TableCell>
                            <TableCell>{emp.email}</TableCell>
                            <TableCell><Badge variant="outline">{emp.department}</Badge></TableCell>
                            <TableCell>
                              {emp.municipality ? (
                                <Badge variant="secondary" className="text-xs">
                                  {emp.municipality === 'ciudad_real' ? 'Ciudad Real' : 
                                   emp.municipality === 'bolanos' ? 'Bolaños' : 
                                   emp.municipality === 'almagro' ? 'Almagro' : 
                                   emp.municipality === 'pozuelo' ? 'Pozuelo' : 
                                   emp.municipality === 'other' ? 'Otra' : emp.municipality}
                                </Badge>
                              ) : (
                                <span className="text-xs text-amber-600">Sin asignar</span>
                              )}
                            </TableCell>
                            <TableCell><Badge>{emp.role}</Badge></TableCell>
                            <TableCell>
                              {emp.role === 'employee' ? (
                                emp.manager_name ? (
                                  <span className="text-sm text-green-600">{emp.manager_name}</span>
                                ) : (
                                  <span className="text-sm text-amber-600">Sin asignar</span>
                                )
                              ) : (
                                <span className="text-sm text-slate-400">N/A</span>
                              )}
                            </TableCell>
                            <TableCell>{emp.used_vacation_days}/{emp.vacation_days}</TableCell>
                            <TableCell>
                              {emp.role === 'employee' ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleReminder(emp.id, emp.hours_reminder_enabled)}
                                  className={emp.hours_reminder_enabled ? "text-green-600" : "text-slate-400"}
                                  title={emp.hours_reminder_enabled ? "Recordatorios activados" : "Recordatorios desactivados"}
                                >
                                  {emp.hours_reminder_enabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                                </Button>
                              ) : (
                                <span className="text-sm text-slate-400">N/A</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" onClick={() => handleEditEmployee(emp)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setDeleteConfirm(emp)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Edit Employee Dialog */}
                <Dialog open={!!editingEmployee} onOpenChange={() => setEditingEmployee(null)}>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Editar Empleado</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Nombre</Label>
                        <Input value={employeeEditForm.name || ''} onChange={(e) => setEmployeeEditForm({...employeeEditForm, name: e.target.value})} />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input value={employeeEditForm.email || ''} onChange={(e) => setEmployeeEditForm({...employeeEditForm, email: e.target.value})} />
                      </div>
                      <div>
                        <Label>Departamento</Label>
                        <Input value={employeeEditForm.department || ''} onChange={(e) => setEmployeeEditForm({...employeeEditForm, department: e.target.value})} />
                      </div>
                      <div>
                        <Label>Rol</Label>
                        <Select 
                          value={employeeEditForm.role || 'employee'} 
                          onValueChange={(value) => setEmployeeEditForm({...employeeEditForm, role: value, manager_id: value !== 'employee' ? '' : employeeEditForm.manager_id})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="employee">Empleado</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {employeeEditForm.role === 'employee' && (
                        <div>
                          <Label>Manager Asignado</Label>
                          <Select 
                            value={employeeEditForm.manager_id || 'none'} 
                            onValueChange={(value) => setEmployeeEditForm({...employeeEditForm, manager_id: value === 'none' ? '' : value})}
                          >
                            <SelectTrigger data-testid="edit-employee-manager-select">
                              <SelectValue placeholder="Selecciona un manager" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Sin manager asignado</SelectItem>
                              {availableManagers.map((manager) => (
                                <SelectItem key={manager.id} value={manager.id}>
                                  {manager.name} ({manager.department})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-slate-500 mt-1">
                            El manager recibirá las solicitudes de vacaciones de este empleado.
                          </p>
                        </div>
                      )}
                      <div>
                        <Label>Población (Festivos Locales)</Label>
                        <Select 
                          value={employeeEditForm.municipality || 'none'} 
                          onValueChange={(value) => setEmployeeEditForm({...employeeEditForm, municipality: value === 'none' ? '' : value})}
                        >
                          <SelectTrigger data-testid="edit-employee-municipality-select">
                            <SelectValue placeholder="Selecciona población" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin asignar (sin festivos locales)</SelectItem>
                            <SelectItem value="ciudad_real">Ciudad Real</SelectItem>
                            <SelectItem value="bolanos">Bolaños de Calatrava</SelectItem>
                            <SelectItem value="almagro">Almagro</SelectItem>
                            <SelectItem value="pozuelo">Pozuelo de Calatrava</SelectItem>
                            <SelectItem value="other">Otra población</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500 mt-1">
                          Solo se aplicarán los festivos locales de esta población al calcular vacaciones.
                        </p>
                      </div>
                      <div>
                        <Label>Días de Vacaciones</Label>
                        <Input type="number" value={employeeEditForm.vacation_days || ''} onChange={(e) => setEmployeeEditForm({...employeeEditForm, vacation_days: parseInt(e.target.value)})} />
                      </div>
                      <div>
                        <Label>Nueva Contraseña (opcional)</Label>
                        <Input type="password" value={employeeEditForm.new_password || ''} onChange={(e) => setEmployeeEditForm({...employeeEditForm, new_password: e.target.value})} placeholder="Dejar vacío para mantener la actual" />
                      </div>
                      <Button onClick={handleUpdateEmployee} disabled={loading} className="w-full">Guardar Cambios</Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Confirmar Eliminación</DialogTitle>
                    </DialogHeader>
                    <p>¿Estás seguro de eliminar a {deleteConfirm?.name}? Esta acción no se puede deshacer.</p>
                    <div className="flex gap-2 mt-4">
                      <Button variant="destructive" onClick={() => handleDeleteEmployee(deleteConfirm)} disabled={loading}>Eliminar</Button>
                      <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>
    </div>
  );
}

export default App;
